// Activity events emitted by `content/comments`.
//
// Same conventions as `articles/events.ts` and `annotations/events.ts`:
// dot-segment kind strings, `COMMENT_ACTIVITY_KINDS` is the source-of-
// truth constant, every emit site references it.
//
// Payload mask discipline: the activity row is durable + fans out to
// many subscribers (notifications dispatcher, future digest, search
// reindex, future analytics). We inline only ids + audit fields. The
// comment's `body` (free-form user text) is NOT inlined — subscribers
// re-resolve through the Comment linkable for current shape, and the
// body might have been redacted by a moderator between publish + read.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Kind constants.
// ---------------------------------------------------------------------------
export const COMMENT_ACTIVITY_KINDS = {
    Created: 'comment.created',
    Updated: 'comment.updated',
    Approved: 'comment.approved',
    Hidden: 'comment.hidden',
    Spam: 'comment.spam',
    Deleted: 'comment.deleted',
    Reported: 'comment.reported',
    ReportResolved: 'comment.report-resolved',
    Locked: 'comment.locked',
    Unlocked: 'comment.unlocked',
    Subscribed: 'comment.subscribed',
    Unsubscribed: 'comment.unsubscribed',
    ModerationSettingsUpdated: 'comment.moderation-settings-updated',
};
/** Glob pattern matching every comment-emitted kind. Used by subscribers
 *  that want a coarse "tell me about anything comment-shaped"
 *  subscription (notifications dispatcher, future digest). */
export const COMMENT_ACTIVITY_KIND_GLOB = 'comment.*';
// ---------------------------------------------------------------------------
// Per-kind payload schemas.
// ---------------------------------------------------------------------------
const Iso = z.string().datetime({ offset: true });
export const CommentCreatedEvent = z.object({
    type: z.literal('comment.created'),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    /** Authenticated author's UUID; null for guest comments. */
    authorId: z.string().uuid().nullable(),
    /** Reply target — null for top-level. The NotificationSubject reads
     *  this directly from the payload to fan-out to the parent author
     *  without a re-fetch. */
    parentId: z.string().uuid().nullable(),
    /** UUIDs of mentioned users (extracted from `@displayName` tokens at
     *  submit time). The dispatcher fans out from this list. */
    mentions: z.array(z.string().uuid()),
    /** Initial moderation status — `'pending'` (mode=pre-approve OR
     *  blocklist `flag`), `'visible'` (mode=post + clean), `'hidden'`
     *  (blocklist `hide`), `'spam'` (external spam classifier).
     *  Subscribers can decide whether to skip notifications for
     *  non-visible rows. */
    initialStatus: z.enum(['pending', 'visible', 'hidden', 'spam']),
    /** Whether the comment row was inserted as a guest — surfaced for
     *  abuse analytics; the dispatcher can drop it or mute notifications
     *  per site policy. */
    isGuest: z.boolean(),
    occurredAt: Iso,
});
export const CommentUpdatedEvent = z.object({
    type: z.literal('comment.updated'),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid().nullable(),
    byUserId: z.string().uuid(),
    changedFields: z.array(z.string()),
    occurredAt: Iso,
});
export const CommentApprovedEvent = z.object({
    type: z.literal('comment.approved'),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid().nullable(),
    approvedBy: z.string().uuid(),
    occurredAt: Iso,
});
export const CommentHiddenEvent = z.object({
    type: z.literal('comment.hidden'),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid().nullable(),
    byUserId: z.string().uuid(),
    reason: z.string(),
    occurredAt: Iso,
});
export const CommentSpamEvent = z.object({
    type: z.literal('comment.spam'),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid().nullable(),
    byUserId: z.string().uuid(),
    reason: z.string(),
    occurredAt: Iso,
});
export const CommentDeletedEvent = z.object({
    type: z.literal('comment.deleted'),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid().nullable(),
    byUserId: z.string().uuid(),
    /** True when the actor is the comment author (own delete); false when
     *  a moderator deleted. Frontend renders different placeholder text
     *  for the two. */
    selfDelete: z.boolean(),
    occurredAt: Iso,
});
export const CommentReportedEvent = z.object({
    type: z.literal('comment.reported'),
    reportId: z.string().uuid(),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    /** Authenticated reporter; null for guest reports. The dispatcher
     *  may dedup notifications by reporter id. */
    reporterId: z.string().uuid().nullable(),
    reason: z.string(),
    occurredAt: Iso,
});
export const CommentReportResolvedEvent = z.object({
    type: z.literal('comment.report-resolved'),
    reportId: z.string().uuid(),
    commentId: z.string().uuid(),
    articleId: z.string().uuid(),
    byUserId: z.string().uuid(),
    outcome: z.enum(['resolved', 'dismissed']),
    occurredAt: Iso,
});
export const CommentLockedEvent = z.object({
    type: z.literal('comment.locked'),
    articleId: z.string().uuid(),
    byUserId: z.string().uuid(),
    reason: z.string().nullable(),
    occurredAt: Iso,
});
export const CommentUnlockedEvent = z.object({
    type: z.literal('comment.unlocked'),
    articleId: z.string().uuid(),
    byUserId: z.string().uuid(),
    occurredAt: Iso,
});
export const CommentSubscribedEvent = z.object({
    type: z.literal('comment.subscribed'),
    articleId: z.string().uuid(),
    userId: z.string().uuid(),
    occurredAt: Iso,
});
export const CommentUnsubscribedEvent = z.object({
    type: z.literal('comment.unsubscribed'),
    articleId: z.string().uuid(),
    userId: z.string().uuid(),
    occurredAt: Iso,
});
export const CommentModerationSettingsUpdatedEvent = z.object({
    type: z.literal('comment.moderation-settings-updated'),
    articleId: z.string().uuid(),
    byUserId: z.string().uuid(),
    changedFields: z.array(z.string()),
    occurredAt: Iso,
});
//# sourceMappingURL=events.js.map